import React, {useId, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import './BookingSelect.css';

export function bookingSelectPosition(rect, viewport, count) {
  const margin = 12, gap = 6;
  const width = Math.min(Math.max(rect.width, 220), viewport.width - margin * 2);
  const left = Math.max(viewport.left + margin, Math.min(rect.left, viewport.left + viewport.width - width - margin));
  const bottom = viewport.top + viewport.height - margin;
  const below = Math.max(0, bottom - rect.bottom - gap);
  const above = Math.max(0, rect.top - viewport.top - margin - gap);
  const desired = Math.min(300, count * 48 + 14);
  const upward = below < desired && above > below;
  const maxHeight = Math.min(desired, upward ? above : below);
  return {left, width, maxHeight, top: upward ? rect.top - gap - maxHeight : rect.bottom + gap};
}

// A body portal escapes the booking workspace's scrolling/clipping containers.
// Keep legacy trigger classes, but isolate the popup from old inline menu CSS.
export default function BookingSelect({value, options = [], onChange, disabled = false, ariaLabel = 'Seleccionar', className = '', displayValue}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const trigger = useRef(null), menu = useRef(null);
  const id = useId();
  const selected = options.find(option => String(option.value) === String(value)) || options[0] || {label: '—'};
  const close = (restore = true) => {setOpen(false); if (restore) trigger.current?.focus();};

  useLayoutEffect(() => {
    if (!open || disabled) return undefined;
    const place = () => {
      const viewport = window.visualViewport;
      const layer = trigger.current?.closest('.loop-accessible-modal-layer');
      const zIndex = layer ? Number(window.getComputedStyle(layer).zIndex) + 10 : 5200;
      setPosition({...bookingSelectPosition(trigger.current.getBoundingClientRect(), {
        left: viewport?.offsetLeft || 0, top: viewport?.offsetTop || 0,
        width: viewport?.width || window.innerWidth, height: viewport?.height || window.innerHeight,
      }, options.length), zIndex: Number.isFinite(zIndex) ? zIndex : 5200});
    };
    place();
    const outside = event => {
      if (!trigger.current?.contains(event.target) && !menu.current?.contains(event.target)) close(false);
    };
    const key = event => {
      if (event.key === 'Escape' || event.key === 'Tab') {
        event.preventDefault(); event.stopImmediatePropagation(); close(); return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const items = Array.from(menu.current?.querySelectorAll('[role="option"]:not(:disabled)') || []);
      const current = items.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
        : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    };
    const scroll = event => {if (!menu.current?.contains(event.target)) place();};
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('keydown', key, true);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', place);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);
    };
  }, [open, disabled, options.length]);

  useLayoutEffect(() => {
    if (open && position) menu.current?.querySelector('[aria-selected="true"]')?.focus({preventScroll: true});
  }, [open, Boolean(position)]);

  return <div className={`loop-mini-select ${open && !disabled ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className.includes('loop-payment-status-select') ? `is-payment-status-${String(value || 'pending')}` : ''} ${className}`}>
    <button ref={trigger} type="button" className="loop-mini-select-trigger" disabled={disabled}
      aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open && !disabled} aria-controls={open && !disabled ? id : undefined}
      onClick={() => {setPosition(null); setOpen(!open);}}
      onKeyDown={event => {if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {event.preventDefault(); setOpen(true);}}}>
      <span>{displayValue ?? selected.label}</span><i aria-hidden="true">⌄</i>
    </button>
    {open && !disabled && position && createPortal(<div ref={menu} id={id} role="listbox" aria-label={ariaLabel}
      className="loop-booking-select-popup" style={position}>
      {options.map(option => <button key={option.value} type="button" role="option" disabled={option.disabled}
        aria-selected={String(option.value) === String(value)} onClick={() => {
          close(); if (String(option.value) !== String(value)) onChange?.(option.value);
        }}><span>{option.label}</span>{String(option.value) === String(value) && <span aria-hidden="true">✓</span>}</button>)}
    </div>, document.body)}
  </div>;
}
